import { Check, CircleAlert, Clock, GitMerge, Lock, Server, Sparkles } from 'lucide-react';
import {
  useEffect,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import { cx } from '../lib/cx.js';
import {
  DEFAULT_FRESHNESS,
  freshnessLevel,
  relativeAge,
  type FreshnessLevel,
  type FreshnessThresholds,
} from '../lib/freshness.js';
import { formatNumber } from '../lib/format.js';
import { Button } from '../primitives/Button.js';
import { Badge, StatusChip, type Tone } from '../primitives/Display.js';
import { Tooltip } from '../primitives/Tooltip.js';
import './shell.css';

/** Re-renders every `ms` so relative times stay current without parents re-rendering. */
function useNow(ms: number, fixed?: Date | number): number {
  const [now, setNow] = useState(() =>
    fixed !== undefined ? new Date(fixed).getTime() : Date.now(),
  );
  useEffect(() => {
    if (fixed !== undefined) {
      setNow(new Date(fixed).getTime());
      return undefined;
    }
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms, fixed]);
  return now;
}

/* ---------- FreshnessBadge ---------- */

const FRESH_TONE: Record<FreshnessLevel, Tone> = {
  live: 'success',
  recent: 'neutral',
  stale: 'warning',
  offline: 'danger',
  unknown: 'neutral',
};

export interface FreshnessBadgeProps {
  /** Last successful sync from the PMS via PulseSync. */
  lastSyncedAt: Date | string | number | null;
  /** Source name, e.g. "Open Dental via PulseSync". */
  source: string;
  thresholds?: FreshnessThresholds;
  /** Fixed clock for tests and stories. */
  now?: Date | number;
  /** Shown in the tooltip, e.g. "3 commands pending write-back". */
  detail?: string;
  onClick?: () => void;
  className?: string;
  /** Extra content after the chip, e.g. a sync progress ring. */
  adornment?: ReactNode;
  /** Off when the badge opens its own popover (the tooltip would cover it). */
  tooltip?: boolean;
  /**
   * For people who cannot act on a late sync (MODULE-AUDIT X5): the chip
   * stays neutral instead of orange or red. The text and tooltip still say
   * how old the data is.
   */
  quiet?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function FreshnessBadge({
  lastSyncedAt,
  source,
  thresholds = DEFAULT_FRESHNESS,
  now,
  detail,
  onClick,
  className,
  adornment,
  tooltip = true,
  quiet = false,
  ref,
  ...rest
}: FreshnessBadgeProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof FreshnessBadgeProps | 'children'>) {
  const current = useNow(15_000, now);
  const level = freshnessLevel(lastSyncedAt, current, thresholds);
  const age = lastSyncedAt === null ? 'never synced' : relativeAge(lastSyncedAt, current);
  const text = level === 'live' ? 'Live' : level === 'unknown' ? 'Not connected' : `Synced ${age}`;
  const tip = `${source}: ${lastSyncedAt === null ? 'never synced' : `last sync ${age}`}${detail ? `. ${detail}` : ''}`;
  const button = (
    <button
      type="button"
      {...rest}
      ref={ref}
      className={cx('vel-fresh', className)}
      data-level={level}
      data-quiet={quiet ? '' : undefined}
      onClick={onClick}
      aria-label={rest['aria-label'] ?? tip}
    >
      <StatusChip
        tone={quiet && level !== 'live' ? 'neutral' : FRESH_TONE[level]}
        label={text}
        live={level === 'live'}
      />
      {adornment}
    </button>
  );
  return tooltip ? <Tooltip content={tip}>{button}</Tooltip> : button;
}

/* ---------- WriteBackDot ---------- */

export type WriteBackState = 'synced' | 'pending' | 'conflict' | 'failed';

const WB: Record<WriteBackState, { tone: Tone; label: string; icon: ReactNode }> = {
  synced: { tone: 'success', label: 'Saved to the PMS', icon: <Check size={10} strokeWidth={3} /> },
  pending: {
    tone: 'info',
    label: 'Waiting to write back to the PMS',
    icon: <Clock size={10} strokeWidth={3} />,
  },
  conflict: {
    tone: 'warning',
    label: 'Changed in the PMS too: needs review',
    icon: <GitMerge size={10} strokeWidth={3} />,
  },
  failed: {
    tone: 'danger',
    label: 'Write-back failed',
    icon: <CircleAlert size={10} strokeWidth={3} />,
  },
};

export interface WriteBackDotProps {
  state: WriteBackState;
  /** Extra tooltip text, e.g. the error or "Queued 2m ago". */
  detail?: string;
  className?: string;
}

/** Per-record write-back state (BUILD-PLAN 2.5.4). Shape differs per state, not only colour. */
export function WriteBackDot({ state, detail, className }: WriteBackDotProps) {
  const s = WB[state];
  const text = detail ? `${s.label}. ${detail}` : s.label;
  return (
    <Tooltip content={text}>
      <span
        className={cx('vel-wb', className)}
        data-tone={s.tone}
        data-state={state}
        role="img"
        aria-label={text}
        tabIndex={0}
      >
        {s.icon}
      </span>
    </Tooltip>
  );
}

/* ---------- EmptyState ---------- */

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  /** Primary and secondary actions. */
  actions?: ReactNode;
  /** Compact variant for inside cards and tables. */
  compact?: boolean;
  className?: string;
}

/** Empty states are a brand moment: the icon sits on the brand gradient. */
export function EmptyState({
  icon,
  title,
  description,
  actions,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div className={cx('vel-empty', className)} data-compact={compact ? '' : undefined}>
      {icon ? (
        <div className="vel-empty__icon" aria-hidden>
          {icon}
        </div>
      ) : null}
      <h3 className="vel-empty__title">{title}</h3>
      {description ? <p className="vel-empty__desc">{description}</p> : null}
      {actions ? <div className="vel-empty__actions">{actions}</div> : null}
    </div>
  );
}

/* ---------- LockedModulePanel ---------- */

export interface LockedModulePanelProps {
  moduleName: string;
  /** One sentence on the outcome, not the feature list. */
  pitch: string;
  features?: readonly string[];
  /** e.g. "$79 per office per month". */
  price?: string;
  trialDays?: number;
  onStartTrial?: () => void;
  onLearnMore?: () => void;
  /** Shown instead of the trial CTA when the user cannot buy (not an owner/admin). */
  requestAccessFrom?: string;
  onRequestAccess?: () => void;
  icon?: ReactNode;
  className?: string;
}

/** Contextual Marketplace upsell shown in place of a module the practice has not enabled. */
export function LockedModulePanel({
  moduleName,
  pitch,
  features = [],
  price,
  trialDays,
  onStartTrial,
  onLearnMore,
  requestAccessFrom,
  onRequestAccess,
  icon,
  className,
}: LockedModulePanelProps) {
  const titleId = useId();
  return (
    <section className={cx('vel-locked', className)} aria-labelledby={titleId}>
      <div className="vel-locked__glow" aria-hidden />
      <div className="vel-locked__icon" aria-hidden>
        {icon ?? <Sparkles size={20} />}
      </div>
      <Badge tone="brand" icon={<Lock size={11} />}>
        Marketplace add-on
      </Badge>
      <h2 id={titleId} className="vel-locked__title">
        {moduleName}
      </h2>
      <p className="vel-locked__pitch">{pitch}</p>
      {features.length > 0 ? (
        <ul className="vel-locked__features">
          {features.map((f) => (
            <li key={f}>
              <Check size={14} aria-hidden />
              {f}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="vel-locked__actions">
        {requestAccessFrom ? (
          <Button variant="primary" onClick={onRequestAccess}>
            Ask {requestAccessFrom} to enable it
          </Button>
        ) : onStartTrial ? (
          <Button variant="hero" size="lg" onClick={onStartTrial}>
            {trialDays ? `Start ${trialDays}-day free trial` : 'Enable'}
          </Button>
        ) : null}
        {onLearnMore ? (
          <Button variant="ghost" onClick={onLearnMore}>
            Learn more
          </Button>
        ) : null}
      </div>
      {price ? <p className="vel-locked__price">{price}. Cancel any time.</p> : null}
    </section>
  );
}

/* ---------- ConnectorHealthCard ---------- */

export type ConnectorStatus = 'healthy' | 'degraded' | 'down' | 'paused';

const CONNECTOR_TONE: Record<ConnectorStatus, Tone> = {
  healthy: 'success',
  degraded: 'warning',
  down: 'danger',
  paused: 'neutral',
};

const CONNECTOR_LABEL: Record<ConnectorStatus, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  down: 'Down',
  paused: 'Paused',
};

export interface ConnectorHealthCardProps {
  officeName: string;
  /** e.g. "Open Dental 23.3" or "Dentrix G7". */
  pms: string;
  status: ConnectorStatus;
  lastHeartbeatAt: Date | string | number | null;
  lastSyncAt: Date | string | number | null;
  /** Write-back commands waiting. */
  queueDepth: number;
  failedCommands?: number;
  agentVersion?: string;
  /** Latest error, already PHI-free. */
  lastError?: string;
  now?: Date | number;
  onOpenDetails?: () => void;
  onRetryFailed?: () => void;
  className?: string;
}

export function ConnectorHealthCard({
  officeName,
  pms,
  status,
  lastHeartbeatAt,
  lastSyncAt,
  queueDepth,
  failedCommands = 0,
  agentVersion,
  lastError,
  now,
  onOpenDetails,
  onRetryFailed,
  className,
}: ConnectorHealthCardProps) {
  const current = useNow(15_000, now);
  const ago = (v: Date | string | number | null) =>
    v === null ? 'never' : relativeAge(v, current);
  return (
    <section className={cx('vel-conn', className)} data-status={status}>
      <header className="vel-conn__head">
        <span className="vel-conn__icon" aria-hidden>
          <Server size={16} />
        </span>
        <div className="vel-conn__titles">
          <h3 className="vel-conn__title">{officeName}</h3>
          <span className="vel-conn__pms">{pms}</span>
        </div>
        <StatusChip
          tone={CONNECTOR_TONE[status]}
          label={CONNECTOR_LABEL[status]}
          live={status === 'healthy'}
        />
      </header>
      <dl className="vel-conn__stats">
        <div>
          <dt>Heartbeat</dt>
          <dd>{ago(lastHeartbeatAt)}</dd>
        </div>
        <div>
          <dt>Last sync</dt>
          <dd>{ago(lastSyncAt)}</dd>
        </div>
        <div>
          <dt>Queue</dt>
          <dd className="vel-num">{formatNumber(queueDepth)}</dd>
        </div>
        <div>
          <dt>Failed</dt>
          <dd className="vel-num" data-bad={failedCommands > 0 ? '' : undefined}>
            {formatNumber(failedCommands)}
          </dd>
        </div>
      </dl>
      {lastError ? (
        <p className="vel-conn__error" role="status">
          <CircleAlert size={13} aria-hidden />
          {lastError}
        </p>
      ) : null}
      <footer className="vel-conn__foot">
        {agentVersion ? <span className="vel-conn__ver">Connector {agentVersion}</span> : <span />}
        <div className="vel-conn__actions">
          {failedCommands > 0 && onRetryFailed ? (
            <Button size="sm" onClick={onRetryFailed}>
              Retry failed
            </Button>
          ) : null}
          {onOpenDetails ? (
            <Button size="sm" variant="ghost" onClick={onOpenDetails}>
              Details
            </Button>
          ) : null}
        </div>
      </footer>
    </section>
  );
}
